import { useNewCode } from '@calendly/feature-check-react';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { ArrowExpandIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { Modal } from '@calendly/ui/components/modal';
import { SimpleSelect } from '@calendly/ui/components/select';
import { HeadingText, BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { useStartNotetakerTrialMutation, useUpdateUserGrowthExperimentsMutation, useGetUserQuery } from 'common';
import { MODAL_ACTION, BILLING_ENTRYPOINTS, MODAL_DETAILS, MODAL_NAMES } from 'features/billing/constants';
import { MI_MEETINGS_TOUR, checkIsFreeEmailDomain } from 'features/meeting_intelligence';
import { useTrack, useTrackOnce } from 'hooks/analytics_events/useTrack';
import RecapImage from '../notetaker_opt_in_modal/assets/recap.png';
import RecapShapesImage from '../notetaker_opt_in_modal/assets/recap_shapes.png';
const NOTETAKER_SCOPE_EXTERNAL = 'external';
const NOTETAKER_SCOPE_ALL = 'all';
const RECAP_EMAIL_RECIPIENTS_ALL = 'all';
const RECAP_EMAIL_RECIPIENTS_ONLY_ME = 'only_me';
const NOTETAKER_DEFAULT_SETTINGS = {
  NOTETAKER_SCOPE: NOTETAKER_SCOPE_EXTERNAL,
  RECAP_EMAIL_RECIPIENTS: RECAP_EMAIL_RECIPIENTS_ALL
};
const NOTETAKER_TRIAL_START_REDIRECT_PATH = '/app/meetings/user/me';
const GROWTH_EXPERIMENTS_OBJECT_KEY = 'nt_free_users_opt_out_experiment';

// Styled components
const modalContentIsPhoneCss = "m1jozsp2";
const modalContentCss = "m1rrwxvt";
const bodyWrapperIsPhoneCss = "bjvs06j";
const bodyWrapperCss = "b1w8zwke";
const leftContentIsPhoneCss = "lbngwes";
const leftContentCss = "l5tort2";
const rightContentCss = "r1bd5m36";
const shapesImageCss = "sxh9x1v";
const imageStyleCss = "i287ktq";
const configSectionCss = "cg3rvm7";
const selectFieldCss = "s1yokqxv";
const mobileScrollableContentIsPhoneCss = "m187izh5";
const mobileScrollableContentCss = "m5rlp7f";
const mobileImageContainerCss = "m13wy7jw";
const mobileImageCss = "m1dajowd";
const buttonsContainerIsPhoneCss = "b3nes4t";
const buttonsContainerCss = "b1ed0h2l";
const mobileButtonCss = "m1symgpy";
const buttonFlexCss = "b41nzrd";
const learnMoreLinkCss = "lio5hff";
const termsLinkCss = "t18fphrm";
const LearnMoreLink = ({
  children
}) => <Link href="https://help.calendly.com/hc/en-us/articles/21652725311383" target="_blank" rel="noopener noreferrer" underline={false} className={learnMoreLinkCss}>
    {children}
    <ArrowExpandIcon />
  </Link>;
const TermsLink = ({
  children
}) => <Link href="https://calendly.com/terms" target="_blank" rel="noopener noreferrer" underline={false} className={termsLinkCss}>
    {children}
  </Link>;
const ModalContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(modalContentCss, {
    [modalContentIsPhoneCss]: isMobile
  }, props.className)} />;
});
const BodyWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(bodyWrapperCss, {
    [bodyWrapperIsPhoneCss]: isMobile
  }, props.className)} />;
});
const LeftContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(leftContentCss, {
    [leftContentIsPhoneCss]: isMobile
  }, props.className)} />;
});
const MobileScrollableContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(mobileScrollableContentCss, {
    [mobileScrollableContentIsPhoneCss]: isMobile
  }, props.className)} />;
});
const ButtonsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isMobile = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(buttonsContainerCss, {
    [buttonsContainerIsPhoneCss]: isMobile
  }, props.className)} />;
});
const RightContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(rightContentCss, props.className)} />;
});
const MobileImageContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(mobileImageContainerCss, props.className)} />;
});
const ConfigSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(configSectionCss, props.className)} />;
});
const SelectField = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(selectFieldCss, props.className)} />;
});
export const NotetakerOptOutModal = () => {
  const isMobile = useDeviceType('isPhone');
  const isAutomatedRecapSharingEnabled = useNewCode('automated_recap_sharing')?.enabled;
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [notetakerScope, setNotetakerScope] = useState(NOTETAKER_DEFAULT_SETTINGS.NOTETAKER_SCOPE);
  const [recapEmailRecipients, setRecapEmailRecipients] = useState(NOTETAKER_DEFAULT_SETTINGS.RECAP_EMAIL_RECIPIENTS);
  const {
    data: userData
  } = useGetUserQuery();
  const isFreeEmail = checkIsFreeEmailDomain(userData?.email);
  const track = useTrack();
  const [startNotetakerTrial] = useStartNotetakerTrialMutation();
  const [updateGrowthExperiments] = useUpdateUserGrowthExperimentsMutation();
  useTrackOnce(true, {
    name: 'Modal Interaction',
    properties: {
      action: MODAL_ACTION.OPENING,
      modal_name: MODAL_NAMES.NOTETAKER_OPT_OUT,
      modal_details: MODAL_DETAILS.NOTETAKER_OPT_OUT,
      location: BILLING_ENTRYPOINTS.NOTETAKER_TRIAL_OPT_OUT_MODAL
    }
  });
  const handleClose = () => {
    updateGrowthExperiments({
      [GROWTH_EXPERIMENTS_OBJECT_KEY]: {
        opted_out: true
      }
    });
    track({
      name: 'Modal Interaction',
      properties: {
        action: MODAL_ACTION.DISMISSED,
        modal_name: MODAL_NAMES.NOTETAKER_OPT_OUT,
        modal_details: MODAL_DETAILS.NOTETAKER_OPT_OUT,
        location: BILLING_ENTRYPOINTS.NOTETAKER_TRIAL_OPT_OUT_MODAL
      }
    });
    setIsOpen(false);
  };
  const handleStartTrial = async () => {
    setIsLoading(true);
    try {
      await startNotetakerTrial({
        notetakerScope,
        recapEmailRecipients
      }).unwrap();
      track({
        name: 'Modal Interaction',
        properties: {
          action: MODAL_ACTION.START_TRIAL,
          modal_name: MODAL_NAMES.NOTETAKER_OPT_OUT,
          modal_details: MODAL_DETAILS.NOTETAKER_OPT_OUT,
          location: BILLING_ENTRYPOINTS.NOTETAKER_TRIAL_OPT_OUT_MODAL
        }
      });
      window.location.href = `${NOTETAKER_TRIAL_START_REDIRECT_PATH}?${MI_MEETINGS_TOUR.STEP_ONE}=true`;
    } catch (error) {
      console.error('Error starting notetaker trial:', error);
      setIsLoading(false);
    }
  };
  return <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <BodyWrapper>
          <LeftContent>
            <MobileScrollableContent>
              <HeadingText level={1}>
                {I18n.t('publisher.notetaker_opt_out.title')}
              </HeadingText>
              <BodyText>
                {HtmlParser(I18n.t('publisher.notetaker_opt_out.description'), {
                LearnMoreLink
              })}
              </BodyText>

              {isMobile && <MobileImageContainer>
                  <img src={RecapImage} alt={I18n.t('publisher.notetaker_opt_in.image_alt')} className={mobileImageCss} />
                </MobileImageContainer>}

              <ConfigSection>
                {!isFreeEmail && <SelectField>
                    <BodyText weight="bold">
                      {I18n.t('publisher.notetaker_opt_in.meeting_scope_label')}
                    </BodyText>
                    <SimpleSelect value={notetakerScope} onChange={setNotetakerScope} size="small" options={[{
                  name: I18n.t('publisher.notetaker_opt_in.scope_options.external'),
                  value: NOTETAKER_SCOPE_EXTERNAL
                }, {
                  name: I18n.t('publisher.notetaker_opt_in.scope_options.all'),
                  value: NOTETAKER_SCOPE_ALL
                }]} />
                  </SelectField>}

                {isAutomatedRecapSharingEnabled && <SelectField>
                    <BodyText weight="bold">
                      {I18n.t('publisher.notetaker_opt_in.email_recipients_label')}
                    </BodyText>
                    <SimpleSelect value={recapEmailRecipients} onChange={setRecapEmailRecipients} size="small" options={[{
                  name: I18n.t('publisher.notetaker_opt_in.recap_email_recipients_options.all'),
                  value: RECAP_EMAIL_RECIPIENTS_ALL
                }, {
                  name: I18n.t('publisher.notetaker_opt_in.recap_email_recipients_options.only_me'),
                  value: RECAP_EMAIL_RECIPIENTS_ONLY_ME
                }]} />
                  </SelectField>}
              </ConfigSection>

              <BodyText size="small" color="muted">
                {HtmlParser(I18n.t('publisher.notetaker_opt_in.footer'), {
                TermsLink
              })}
              </BodyText>
            </MobileScrollableContent>

            <ButtonsContainer>
              <Button decoration="secondary-outline" onClick={handleClose} disabled={isLoading} className={classNames(isMobile ? mobileButtonCss : buttonFlexCss)}>
                {I18n.t('publisher.notetaker_opt_out.skip_trial')}
              </Button>
              <Button onClick={handleStartTrial} loading={isLoading} disabled={isLoading} className={classNames(isMobile ? mobileButtonCss : buttonFlexCss)}>
                {I18n.t('publisher.notetaker_opt_out.okay')}
              </Button>
            </ButtonsContainer>
          </LeftContent>

          {!isMobile && <RightContent>
              <img src={RecapImage} alt={I18n.t('publisher.notetaker_opt_in.image_alt')} className={imageStyleCss} />
              <img src={RecapShapesImage} alt="" className={shapesImageCss} />
            </RightContent>}
        </BodyWrapper>
      </ModalContent>
    </Modal>;
};

require("./notetaker_opt_out_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./notetaker_opt_out_modal.js");